import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import StatusContainer from '@/components/StatusContainer';
import ToolGrid from '@/components/Tools/ToolGrid';
import { toolsApi } from '@/lib/api/tools';

function CategoryPreview({ category, onCategoryPress, onToolPress, refreshKey }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [tools, setTools] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    toolsApi.list({ category: category.slug, limit: 3 }).then(data => {
      if (!cancelled) setTools(data.tools || []);
    }).catch(() => {
      if (!cancelled) setError(t('pages.tools.previewError'));
    });
    return () => { cancelled = true; };
  }, [category.slug, refreshKey, retry, t]);

  if (tools?.length === 0 && !error) return null;

  return (
    <View style={{ gap: 16 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Text tag="h2" weight="bold" style={{ flex: 1, fontSize: 20, lineHeight: 26, letterSpacing: -0.4 }}>{category.name}</Text>
        <Pressable onPress={() => onCategoryPress(category.slug)} accessibilityRole="button"
          accessibilityLabel={`${t('pages.tools.seeAll')} · ${category.name}`}
          style={({ hovered, pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 44, paddingHorizontal: 10,
            borderRadius: 10, backgroundColor: hovered || pressed ? colors.solid_color_secondary : 'transparent' })}>
          <Text size="s" color="secondary">{t('pages.tools.seeAll')}</Text>
          <Icon name="chevron-right" size={10} color={colors.text_color_secondary} />
        </Pressable>
      </View>
      {error ? <StatusContainer type="error" message={error} actionText={t('pages.common.retry')} onAction={() => setRetry(value => value + 1)} />
        : <ToolGrid preview tools={tools} isLoading={tools === null} placeholderCount={3} onToolPress={onToolPress} />}
    </View>
  );
}

export default function ToolsDirectory({ categories, onCategoryPress, onToolPress, refreshKey }) {
  return (
    <View style={{ gap: 36 }}>
      {categories.map(category => <CategoryPreview key={category.slug} {...{ category, onCategoryPress, onToolPress, refreshKey }} />)}
    </View>
  );
}
